import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from "motion/react";
import { FolderOpen, ArrowRight } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { PageHero } from '../../components/common/PageHero';

interface ProjectDto {
    id: string;
    slug: string;
    icon?: string;
    imageUrl?: string;
    color?: string;
    title: string;
    description: string;
}

export const Projekte: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Fetch page metadata and update title
    const { data: pageMeta } = usePageMetadata('projekte');

    const { data: pagedData, isLoading } = useQuery({
        queryKey: ['publicProjects'],
        queryFn: async () => {
            const response = await apiClient.get('/public/projects');
            return response.data;
        }
    });

    const projects: ProjectDto[] = pagedData?.data || [];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <div className="flex flex-col bg-background min-h-screen">
            {/* Subpage Hero with Cover Photo behind Title */}
            <PageHero
                title={pageMeta?.title || t('projects.title')}
                subtitle={pageMeta?.metaDescription || t('projects.description')}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: pageMeta?.title || t('projects.breadcrumb') }
                ]}
            />

            <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 w-full py-16 sm:py-24">
                {/* Project List: One by one (Tek Tek, Grid Değil) */}
                {isLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                ) : projects && projects.length > 0 ? (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="space-y-8"
                    >
                        {projects.map((project: ProjectDto) => (
                            <motion.div
                                key={project.id}
                                variants={itemVariants}
                                onClick={() => navigate(`/projekte/${project.slug}`)}
                                className="group relative flex flex-col md:flex-row items-stretch justify-between rounded-3xl border border-border bg-background shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg cursor-pointer overflow-hidden"
                            >
                                <div className="w-full md:w-80 h-56 md:h-auto shrink-0 relative overflow-hidden bg-primary/5 flex items-center justify-center">
                                    {project.imageUrl ? (
                                        <img src={resolveAssetUrl(project.imageUrl)} alt={project.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    ) : (
                                        <FolderOpen className="h-16 w-16 text-primary" style={project.color ? { color: project.color } : undefined} />
                                    )}
                                </div>
                                <div className="flex flex-1 flex-col justify-between p-6 sm:p-8">
                                    <div>
                                        <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                                            {project.title}
                                        </h3>
                                        <div className="mt-4 text-base leading-relaxed text-foreground/80 line-clamp-3">
                                            {project.description?.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()}
                                        </div>
                                    </div>
                                    <div className="mt-8 flex items-center justify-end w-full border-t border-border/50 pt-4">
                                        <span className="text-sm font-semibold text-primary group-hover:text-primary-foreground group-hover:bg-primary px-4 py-2 rounded-lg transition-colors border border-transparent group-hover:border-primary flex items-center gap-2">
                                            {t('projects.learn_more')} <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="rounded-3xl border border-dashed border-border bg-secondary/30 p-16 text-center">
                        <h3 className="text-lg font-medium text-foreground">{t('projects.no_projects')}</h3>
                        <p className="mt-2 text-foreground/60">{t('projects.check_back')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
